<template>
  <div class="notif-wrapper" v-on:notification.native="messageReceived">
    <b-form @submit.prevent="save">
      <div v-if="editing">
        <SettingsTitleBar sub="Addresses" @cancel.prevent="cancel"/>
        <b-alert show :variant="notification.type">{{notification.message}}</b-alert>
        <b-form-group label="Address:" label-for="line1" v-bind="validationAttrsFor('line1')">
          <b-form-input v-validate="'required'" name="line1" v-model="editing.line1" id="line1" data-vv-as="address line 1" placeholder="Enter address"/>
        </b-form-group>
        <b-form-group label=" " label-for="line2" v-bind="validationAttrsFor('line2')">
          <b-form-input v-model="editing.line2" name="line2" id="line2" placeholder="Address line 2 - optional"/>
        </b-form-group>
        <b-form-group label=" " label-for="line3" v-bind="validationAttrsFor('line3')">
          <b-form-input v-model="editing.line3" name="line3" id="line3" placeholder="Address line 3 - optional"/>
        </b-form-group>
        <b-form-group label="City:" label-for="city" v-bind="validationAttrsFor('city')">
          <b-form-input v-validate="'required'" v-model="editing.city" name="city" id="city" placeholder="Enter city"/>
        </b-form-group>
        <b-form-group
          :label="isUSA ? 'State:' : 'Province'"
          label-for="state"
          v-bind="validationAttrsFor('state')"
        >
          <b-form-input
            v-validate="{required: isUSA}"
            v-model="editing.state"
            name="state"
            id="state"
            :placeholder="isUSA ? 'Enter state' : 'Enter province'"
          />
        </b-form-group>
        <b-form-group
          :label="isUSA ? 'Zip code:' : 'Postal code:'"
          label-for="postcode"
          v-bind="validationAttrsFor('postcode')"
        >
          <b-form-input v-validate="'required'" v-model="editing.postcode" name="postcode" id="postcode" placeholder="Enter postcode"/>
        </b-form-group>
        <b-form-group
          v-if="flags.internationalShipping"
          label="Country:"
          label-for="country"
          v-bind="validationAttrsFor('country')"
        >
          <b-form-select
            name="country"
            id="country"
            v-validate="'required'"
            v-model="editing.country"
            :options="countries"
            value-field="iso_3166_1_a2"
            text-field="printable_name"
          />
        </b-form-group>
        <b-form-checkbox class="default-shipping" v-model="editing.is_default_for_shipping">
        Save as my default address</b-form-checkbox>
        <b-form-checkbox class="is-private" v-model="is_private">
        Save as a private address (not available to team)</b-form-checkbox>
      </div>

      <div v-else class="address-list">
        <SettingsTitleBar sub="Addresses">
          <!-- <b-button class="btn-secondary" @click="refreshAddresses">Refresh</b-button> -->
          <div class="add-address btn btn-primary" @click="add">Add New</div>
          <b-button class="btn-secondary" @click="quit">Cancel</b-button>
        </SettingsTitleBar>
        <b-alert show :variant="notification.type">{{notification.message}}</b-alert>
        <div class="card" v-for="(address, index) in combinedAddresses" :key="address.id + '_' + index">
          <Address @click="edit(address)" class="pointer" :value="address" />
          <div class="card-buttons">
            <AddressVerification :address="address" :on-verification-finish="refreshAddresses"/>
            <div @click="favorite(address)" class="pointer" v-b-tooltip.hover title="Make default address" v-html="favoriteIconHtml(address)"></div>
            <div @click="edit(address)" class="pointer" v-b-tooltip.hover title="Edit address"><i class="fa fa-edit"/></div>
            <div @click="del(address)" class="pointer" v-b-tooltip.hover title="Delete address"><i class="fa fa-trash"/></div>
            <div v-if="address.type === 'team'" v-b-tooltip.hover title="Team address"><i class="fas fa-users"/></div>
            <div v-else v-b-tooltip.hover title="Personal address"><i class="fas fa-user"/></div>
          </div>
        </div>
        <div class="add-address btn btn-primary" @click="add">Create New Address</div>
      </div>
    </b-form>
  </div>
</template>

<style scoped lang="scss">
@import '~scss/colors';
.card {
  margin: 1em;
  padding: 0.61em;
  background: $white;
}
.card:hover{
   border-color: #333;
  background: #f8f8f8;
}
.card-buttons {
  position: absolute;
  right: 1em;
  text-align: right;
  width: 12em;
  color: #aaa;
  > div {
    display: inline;
  }
}
.pointer {
  cursor: pointer;
}

.b-form-group{
  border:none;
  margin:0;
  padding:0;
}

.default-shipping{
  margin-top: 1em;
}

.add-address.btn.btn-primary{
  line-height:20px;
}

.btn-primary {
  background: $red;
  color:$white;
  border: $red 1px solid;
  line-height: 20px;
}

.btn-secondary {
  background: $white;
  color:$red;
  border: $red 1px solid;
  line-height: 20px;
}

</style>

<script>
  import {mapActions, mapGetters} from "vuex";
  import {isUSA} from '@/lib/CheckoutLogic';
  import AddressVerification from '@/components/shared/AddressVerification';

  export default {
  components: {
    AddressVerification,
  },
  data() {
    return {
      editing: null,
      notification: {message: '', type: ''},
      saveErrors: {}
    };
  },
  mounted() {
    this.loadUserAddresses();
    this.loadTeamAddresses();

    if (this.flags.internationalShipping && this.countries.length === 0) {
      this.loadCountries();
    }
  },
  computed: {
    ...mapGetters(["combinedAddresses", "flags", "countries"]),
    is_private: {
      get() {
        if (!this.editing) return;
        return this.editing.type !== 'team'; // Treat missing values as user addresses
      },
      set(val) {
        if (!this.editing) return;
        this.editing.type = val ? 'user' : 'team';
      }
    },
    isUSA() {
      return isUSA(this.editing.country);
    },
  },
  methods: {
    ...mapActions([
      "removeAddress", "addAddress", "updateAddress", "loadTeamAddresses", "loadUserAddresses",
      "refreshAddresses", "setDefaultUserAddress", "loadCountries",
    ]),
    edit(address) {
      this.editing = Object.assign({}, address);
      this.editing.origType = this.editing.type;
      this.notification = {};
    },
    async del(address) {
      try{
        await this.removeAddress(address);
        this.notification = {message: "Address deleted", type: "success"};
      } catch(e) {
        console.error(e);
        this.notification = {message: "Error deleting address: " + e, type: "danger"};
      }
    },
    cancel(event) {
      this.editing = null;
      this.notification = {};
    },
    quit() {
      this.$router.push('/dashboard/settings')
      this.notification = {};
    },
    add() {
      this.editing = {type: 'team', adding: true, country: "US"};
      this.notification = {};
    },
    async save(event) {
      if (!this.editing) return;
      let valid = await this.$validator.validate();
      if (!valid) {
        console.log(valid);
        console.log(this.errors.all());
        this.notification = {message: "Please fix the errors below", type: 'danger'};
        return;
      }
      let action = this.editing.adding ? 'addAddress' : 'updateAddress';
      try {
        await this[action](this.editing);
        this.editing = null;
      } catch(e) {
        if (e instanceof Error || typeof e === "string") {
          this.notification = {message: "Error saving address: "+e, type: "danger"};
        } else {
          // error is a list of invalid field states
          this.saveErrors = e;
        }
      }
    },
    async favorite(address) {
      await this.setDefaultUserAddress(address);
    },
    favoriteIconHtml({is_default_for_shipping}) {
      // Work around font-not-awesome failing to notice class changes by
      // returning HTML elements
      let cls = is_default_for_shipping ? 'fas' : 'far';
      return `<i class="${cls} fa-star"></i>`;
    },
    messageReceived(event) {
      console.log('messageReceived');
      this.notification = event.detail;
    },
    validationAttrsFor(fieldname) {
      let feedback, state;

      let err = this.saveErrors[fieldname] || this.errors.first(fieldname);

      feedback = err;
      state = !err;  // TODO maybe return null when v-validate "untouched" flag or similar

      // console.log(this.errors);
      // console.log('validationAttrsFor', fieldname, feedback, state);

      return { state, 'invalid-feedback': feedback };
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/pages/dashboard/settings/EditAddresses.vue